<template>
  <nav
    class="flex items-center justify-between border-t mt-4 border-surface-weak px-4 sm:px-0 md:px-4 lg:px-4 "
    aria-label="Pagination"
    data-test-id="am3-pagination"
  >
    <div class="flex-1" />

    <div class="flex items-center gap-6">
      <!-- Rows per page select -->
      <div
        v-if="pageSizeOptions && pageSizeOptions.length"
        class="flex items-center gap-2"
      >
        <p class="text-sm font-medium text-secondary whitespace-nowrap">
          {{ dictionary }} per page
        </p>
        <select
          v-model="localPageSize"
          :disabled="loading"
          class="h-8 rounded-md border border-surface-dark bg-surface-light px-2 py-1 text-sm text-primary outline-none focus:outline-brand focus:outline-offset-0 focus:outline-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          @change="handlePageSizeChange"
        >
          <option
            v-for="size in pageSizeOptions"
            :key="size"
            :value="size"
          >
            {{ size }}
          </option>
        </select>
      </div>

      <!-- Page X of Y (clickable to jump) -->
      <AM3PageSelect
        :total-pages="totalPages"
        :current-page="modelValue"
        :disabled="loading"
        @select="goToPage"
      >
        <span class="text-sm font-medium text-secondary whitespace-nowrap">
          Page {{ modelValue }} of {{ totalPages }}
        </span>
      </AM3PageSelect>

      <!-- Navigation buttons -->
      <div class="flex items-center gap-1">
        <button
          :disabled="loading || modelValue === 1"
          type="button"
          aria-label="First page"
          :class="buttonClasses"
          @click="goToPage(1)"
        >
          <AM3Icon
            type="chevron-double-left"
            size="sm"
          />
        </button>

        <button
          :disabled="loading || modelValue === 1"
          type="button"
          aria-label="Previous page"
          data-test-id="am3-pagination-prev"
          :class="buttonClasses"
          @click="goToPage(modelValue - 1)"
        >
          <AM3Icon
            type="chevron-left"
            size="sm"
          />
        </button>

        <button
          :disabled="loading || modelValue === totalPages"
          type="button"
          aria-label="Next page"
          data-test-id="am3-pagination-next"
          :class="buttonClasses"
          @click="goToPage(modelValue + 1)"
        >
          <AM3Icon
            type="chevron-right"
            size="sm"
          />
        </button>

        <button
          :disabled="loading || modelValue === totalPages"
          type="button"
          aria-label="Last page"
          :class="buttonClasses"
          @click="goToPage(totalPages)"
        >
          <AM3Icon
            type="chevron-double-right"
            size="sm"
          />
        </button>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import AM3PageSelect from './AM3PageSelect.vue'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import { usePaginationNav } from '@/composables/usePaginationNav'

interface Props {
  modelValue: number // Current page (1-based)
  totalPages: number // Total number of pages
  pageSize?: number // Current page size
  pageSizeOptions?: null | number[] // Available page size options
  dictionary?: string // Plural label for the page-size selector, e.g. 'Contacts'
  loading?: boolean // Disables page/size controls while a fetch is in flight
}

const buttonClasses = 'inline-flex hover:bg-surface focus:outline-surface-dark outline outline-transparent outline-offset-1 outline-1 items-center justify-center h-8 w-8 rounded-md border border-surface-dark text-secondary hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-secondary transition-colors'

interface Emits {
  (e: 'update:modelValue', value: number): void
  (e: 'change', value: number): void
  (e: 'update:pageSize', value: number): void
}

const props = withDefaults(defineProps<Props>(), {
  pageSize: 50,
  pageSizeOptions: null,
  dictionary: 'Contacts',
  loading: false,
})

const emit = defineEmits<Emits>()

const localPageSize = ref(props.pageSize)

watch(() => props.pageSize, (val) => {
  localPageSize.value = val
})

const { goToPage } = usePaginationNav(
  () => props.modelValue,
  () => props.totalPages,
  (page) => {
    emit('update:modelValue', page)
    emit('change', page)
  },
)

const handlePageSizeChange = () => {
  emit('update:pageSize', localPageSize.value)
}
</script>
